/**
 * Country Conversion Utilities
 *
 * The LMI user profile and the calculator APIs identify a country by its numeric id, while the
 * shared ad feeds on the CDN target countries by ISO 3166-1 alpha-2 code. This module is the one
 * place that knows how the two line up.
 */

/**
 * Every country the platform issues an id for, as returned by the `/country` endpoint.
 * Papua New Guinea is served but its id is not known here, so it has no entry yet.
 */
export const COUNTRIES = [
  { id: 1, code: 'au', name: 'Australia' },
  { id: 2, code: 'nz', name: 'New Zealand' },
  { id: 3, code: 'gb', name: 'United Kingdom' },
  { id: 5, code: 'id', name: 'Indonesia' },
  { id: 6, code: 'za', name: 'South Africa' },
  { id: 7, code: 'ca', name: 'Canada' },
  { id: 8, code: 'sg', name: 'Singapore' },
  { id: 9, code: 'us', name: 'USA' },
  { id: 10, code: 'ie', name: 'Ireland' },
  { id: 11, code: 'hk', name: 'Hong Kong' },
  { id: 12, code: 'my', name: 'Malaysia' },
  { id: 56, code: 'fj', name: 'Fiji' },
  { id: 104, code: 'na', name: 'Namibia' },
  { id: 137, code: 'th', name: 'Thailand' },
];

/**
 * Codes that mean a country we already know under its ISO code. Feeds are hand-authored, so the
 * United Kingdom in particular arrives spelled either way.
 */
const CODE_ALIASES = {
  uk: 'gb',
};

const byId = COUNTRIES.reduce(
  (acc, country) => ({ ...acc, [country.id]: country }),
  {}
);

const byCode = COUNTRIES.reduce(
  (acc, country) => ({ ...acc, [country.code]: country }),
  {}
);

/**
 * Normalises a country code as written in a feed - trimmed, lower case, aliases resolved.
 * @param {string} countryCode - A country code, in any casing
 * @returns {string|null} - The canonical ISO code, or null when there is nothing to normalise
 */
export const normaliseCountryCode = (countryCode) => {
  if (!countryCode) return null;

  const code = String(countryCode).trim().toLowerCase();
  if (!code) return null;

  return CODE_ALIASES[code] || code;
};

/**
 * Converts a platform country id to the ISO code the ad feeds use.
 * @param {number|string} countryId - The numeric country id from the user profile
 * @returns {string|null} - The lower case ISO code, or null for an unmapped id
 */
export const toCountryCode = (countryId) => byId[countryId]?.code ?? null;

/**
 * Converts an ISO country code back to the platform's numeric id.
 * @param {string} countryCode - An ISO 3166-1 alpha-2 code, in any casing
 * @returns {number|null} - The numeric country id, or null for an unmapped code
 */
export const toCountryId = (countryCode) =>
  byCode[normaliseCountryCode(countryCode)]?.id ?? null;

/**
 * The display name for a country id, for labelling rather than for logic.
 * @param {number|string} countryId - The numeric country id
 * @returns {string|null} - The country's name, or null for an unmapped id
 */
export const getCountryName = (countryId) => byId[countryId]?.name ?? null;

/**
 * Whether two countries are the same, given either an id or a code on each side. Useful where a
 * feed and a user profile disagree about which form they speak.
 * @param {number|string} countryId - The numeric country id
 * @param {string} countryCode - An ISO 3166-1 alpha-2 code, in any casing
 * @returns {boolean} - True only when both resolve to the same known country
 */
export const isSameCountry = (countryId, countryCode) => {
  const code = toCountryCode(countryId);
  return !!code && code === normaliseCountryCode(countryCode);
};
